import React, { memo, useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { DEFAULT_COORDS, TOP } from './consts'
import { getCoordsAndPosition } from './utils'
import * as Styled from './styled'
import type { IProps } from './types'

export const Tooltip = React.forwardRef<HTMLElement, IProps>(
  (
    {
      children,
      tooltipText,
      position: preferredPosition = TOP,
      isVisible,
      ...rest
    },
    forwardedRef
  ) => {
    const [internalIsVisible, setInternalIsVisible] = useState(false)
    const [coords, setCoords] = useState(DEFAULT_COORDS)
    const [position, setPosition] = useState(preferredPosition)

    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const tooltipRef = useRef<HTMLDivElement | null>(null)

    const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

    // isVisible=true: enable hover, isVisible=false: disable completely, undefined: normal hover
    const canShowTooltip = isVisible !== false
    const tooltipIsVisible = canShowTooltip && internalIsVisible

    const showTooltip = () => {
      if (isMobile || !canShowTooltip) return
      setInternalIsVisible(true)
    }

    const hideTooltip = () => {
      if (!canShowTooltip) return
      setInternalIsVisible(false)
    }

    useEffect(() => {
      if (!tooltipIsVisible) return

      const { x, y, position } = getCoordsAndPosition(
        tooltipRef,
        wrapperRef,
        preferredPosition
      )

      setCoords({ x, y })
      setPosition(position)
    }, [tooltipIsVisible, tooltipRef, wrapperRef, preferredPosition])
    return (
      <>
        {React.cloneElement(children, {
          onMouseEnter: showTooltip,
          onMouseLeave: hideTooltip,
          ref: (node: HTMLDivElement) => {
            // Handle both the forwarded ref and the internal wrapperRef
            if (typeof forwardedRef === 'function') {
              forwardedRef(node)
            } else if (forwardedRef) {
              forwardedRef.current = node
            }
            wrapperRef.current = node
          }
        })}
        {tooltipIsVisible && !isMobile && !!tooltipText
          ? createPortal(
              <AnimatePresence>
                <Styled.Tooltip
                  ref={tooltipRef}
                  position={position}
                  coords={coords}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  {...rest}
                >
                  {tooltipText}
                  <div className="triangle" />
                </Styled.Tooltip>
              </AnimatePresence>,
              document.body
            )
          : null}
      </>
    )
  }
)

Tooltip.displayName = 'Tooltip'

export default memo(Tooltip)
